/**
 * Unreferenced-objects REPORT (super_admin) — read-only diagnostic listing bucket
 * objects that no DB record references. It does NOT delete anything.
 *
 * ⚠️ The destructive "sweep/delete" was removed (May 2026 incident): the bucket is
 * shared across environments, so a single-DB orphan scan must never delete. File
 * removal only happens via authorized, human-triggered actions in the app
 * (deleting a module / slide / file).
 */
import { Loader2, ScanSearch, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatBytes } from '../../../utils/uploadImage';

export function BucketSweepPanel({ isSuperAdmin, sweepPreview, sweepLoading, onPreview }) {
  if (!isSuperAdmin) return null;

  const hasUnknown = sweepPreview && sweepPreview.unknown_count > 0;

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="bucket-sweep-card">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ScanSearch className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold">Unreferenced objects (report)</h3>
          <span className="text-xs text-muted-foreground">read-only · nothing is deleted</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onPreview}
          disabled={sweepLoading}
          data-testid="bucket-sweep-preview-btn"
        >
          {sweepLoading
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <ScanSearch className="w-4 h-4 mr-1.5" />}
          {sweepPreview ? 'Re-scan' : 'Scan'}
        </Button>
      </div>

      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Diagnostic only. This bucket is shared across environments, so an object with no
          reference <span className="text-foreground font-medium">here</span> may still be in use
          elsewhere. The app never deletes files automatically — removal happens only when an
          authorized editor/admin deletes a module, slide, or file in the app.
        </p>
      </div>

      {!sweepPreview && !sweepLoading && (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          Scans the bucket and cross-checks every object against this environment&apos;s DB
          references. Use it to understand storage drift — it will never delete anything.
        </div>
      )}

      {sweepLoading && (
        <div className="px-4 py-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning bucket and building the known-keys index…
        </div>
      )}

      {sweepPreview && (
        <div className="px-4 py-4 space-y-4" data-testid="bucket-sweep-preview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Scanned" value={sweepPreview.scanned_objects.toLocaleString()} />
            <Stat label="Referenced here" value={sweepPreview.known_key_count.toLocaleString()} />
            <Stat
              label="Unreferenced"
              value={sweepPreview.unknown_count.toLocaleString()}
              testid="bucket-sweep-unknown-count"
              tone={hasUnknown ? 'amber' : 'default'}
            />
            <Stat label="Their size" value={formatBytes(sweepPreview.unknown_bytes)} tone={hasUnknown ? 'amber' : 'default'} />
          </div>

          <div className="text-xs text-muted-foreground">
            {sweepPreview.protected_recent_count > 0 && (
              <span data-testid="bucket-sweep-protected">
                {sweepPreview.protected_recent_count.toLocaleString()} recent object
                {sweepPreview.protected_recent_count === 1 ? '' : 's'} (modified &lt; {sweepPreview.min_age_hours}h).{' '}
              </span>
            )}
            {sweepPreview.truncated && (
              <span>Report capped at {sweepPreview.cap.toLocaleString()} objects.</span>
            )}
          </div>

          {sweepPreview.sample?.length > 0 && (
            <details className="rounded border bg-muted/30 px-3 py-2" data-testid="bucket-sweep-sample">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground select-none">
                Sample of {sweepPreview.sample.length} unreferenced keys
              </summary>
              <ul className="mt-2 space-y-0.5 max-h-48 overflow-auto">
                {sweepPreview.sample.map((s) => (
                  <li key={s.key} className="text-[11px] font-mono flex items-center justify-between gap-3">
                    <span className="truncate" title={s.key}>{s.key}</span>
                    <span className="text-muted-foreground shrink-0 tabular-nums">{formatBytes(s.size_bytes)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, tone = 'default', testid }) {
  return (
    <div className="rounded-lg border p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`text-xl font-display font-bold tabular-nums mt-1 ${tone === 'amber' ? 'text-amber-600' : ''}`}>{value}</div>
    </div>
  );
}
